import { useState } from 'react';
import { CheckCircle2, Loader2, Send, MessageCircle } from 'lucide-react';
import { captureAttribution } from '../../utils/attribution';
import { CONTACT_INFO } from '../../data/siteContent';

import { trackLeadConversion } from '../../utils/analytics';
import { COUNTRY_CODES, isPlausiblePhone, toInternationalDigits } from '../../utils/phone';
import { useVoiceConsent, VoiceConsentCheckbox } from './VoiceConsent';
/**
 * Shared "Investment Summary" request form — used by BOTH:
 *  - the development-page section (formContext="development", carries the
 *    development so Cindy's opener + summary are scoped to it), and
 *  - the /millionaire-strategy lander (formContext="millionaire_strategy").
 *
 * Posts to the captureInvestmentSummaryRequest Cloud Function. The single
 * WhatsApp-number field doubles as the contact's mobile number by design —
 * the form explicitly promises WhatsApp delivery, so the number given IS the
 * WhatsApp number. Country-code normalization follows ViewingForm.
 */


const inputClass =
  'w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary bg-white';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

interface InvestmentSummaryFormProps {
  formContext: 'development' | 'millionaire_strategy' | 'qualify_check' | 'family_pty';
  developmentId?: string;
  developmentName?: string;
  /** Note shown under the number field — CMS-editable on both surfaces */
  whatsappNote?: string;
  /** Overrides the success-state body copy (qualify popup promises an
      affordability check first, not just the summary) */
  successMessage?: string;
  /** Called after a successful submit; the page then owns the thank-you
      (the Family Pty lander shows its download panel instead). */
  onSubmitted?: () => void;
  /** Submit button text (default "Send My Investment Summary"). */
  submitLabel?: string;
}

export default function InvestmentSummaryForm({
  submitLabel = 'Send My Investment Summary',
  formContext,
  developmentId,
  developmentName,
  whatsappNote = 'Your investment summary will be delivered to you on WhatsApp.',
  successMessage,
  onSubmitted,
}: InvestmentSummaryFormProps) {
  const [firstName, setFirstName] = useState('');
  const voice = useVoiceConsent();
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('27');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim()) return setError('Please enter your name and surname.');
    if (!isPlausiblePhone(whatsappNumber)) return setError('Please enter a valid WhatsApp number.');
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailOk) return setError('Please enter a valid email address.');

    // Full international digits (shared normaliser — see utils/phone.ts)
    const normalized = toInternationalDigits(whatsappNumber, countryCode);

    setSubmitting(true);
    try {
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      const response = await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/captureInvestmentSummaryRequest`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...voice.payload,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim().toLowerCase(),
            whatsappNumber: normalized,
            formContext,
            ...(developmentId ? { developmentId } : {}),
            ...(developmentName ? { developmentName } : {}),
            attribution: captureAttribution(),
          }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
      setSubmitted(true);
      trackLeadConversion(`investment_summary_${formContext}`, developmentId ? { development_id: developmentId } : undefined);
      onSubmitted?.();
    } catch (err) {
      setError(
        err instanceof Error && err.message !== 'Submission failed'
          ? err.message
          : `Something went wrong — please try again, or call us on ${CONTACT_INFO.phone}.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-card p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-9 h-9 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900">You're all set!</h3>
        <p className="mt-3 text-gray-600 max-w-md mx-auto">
          {successMessage ??
            'Keep an eye on your WhatsApp — your investment summary is on its way to you shortly.'}
        </p>
        <p className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500">
          <MessageCircle className="w-4 h-4 text-[#25D366]" />
          Delivered via WhatsApp
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-card p-6 md:p-8" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="isf-first" className={labelClass}>
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="isf-first"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Your name"
            className={inputClass}
            required
          />
        </div>
        <div>
          <label htmlFor="isf-last" className={labelClass}>
            Surname <span className="text-red-500">*</span>
          </label>
          <input
            id="isf-last"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Your surname"
            className={inputClass}
            required
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="isf-email" className={labelClass}>
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="isf-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className={inputClass}
          required
        />
      </div>

      <div className="mt-4">
        <label htmlFor="isf-whatsapp" className={labelClass}>
          WhatsApp Number <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <select
            aria-label="Country code"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="rounded-lg border border-gray-300 px-2 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            id="isf-whatsapp"
            type="tel"
            value={whatsappNumber}
            onChange={(e) => setWhatsappNumber(e.target.value)}
            placeholder="82 123 4567"
            className={inputClass}
            required
          />
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
          <MessageCircle className="w-4 h-4 text-[#25D366] flex-shrink-0" />
          {whatsappNote}
        </p>
      </div>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      <VoiceConsentCheckbox consent={voice.consent} ticked={voice.ticked} setTicked={voice.setTicked} className="mb-3" />

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold px-8 py-3.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send className="w-4 h-4" /> {submitLabel}
          </>
        )}
      </button>
    </form>
  );
}
